import { Copy, GripVertical, Settings, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getComponentIcon } from "../../shared/component-icons";
import type { PdfComponent, PdfComponentType } from "./types";

// Helper to get component display text from config (title or content preview)
function getComponentDisplayText(component: PdfComponent): string | null {
  if (!component.config) return null;
  try {
    const config = JSON.parse(component.config);
    // For text components, show content preview
    if (component.componentType === "text" && config.content) {
      const text = config.content.substring(0, 20);
      return config.content.length > 20 ? `${text}...` : text;
    }
    // For heading components, show title
    if (component.componentType === "heading" && config.title) {
      const text = config.title.substring(0, 20);
      return config.title.length > 20 ? `${text}...` : text;
    }
    // For other components with title
    if (config.title) {
      const text = config.title.substring(0, 20);
      return config.title.length > 20 ? `${text}...` : text;
    }
    return null;
  } catch {
    return null;
  }
}

/** QL-424 — an enabled `conditionalDisplay` rule in a component or layout `config`. */
export function hasConditionalDisplay(config: string | null): boolean {
  if (!config) return false;
  try {
    return JSON.parse(config)?.conditionalDisplay?.enabled === true;
  } catch {
    return false;
  }
}

// Component card display
export function ComponentCard({
  component,
  onSettings,
  onDelete,
  onDuplicate,
  duplicateLabel,
  settingsLabel,
  deleteLabel,
  conditionalLabel,
  onDragStart,
  onDragEnd,
  getLabel,
}: {
  component: PdfComponent;
  onSettings: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  duplicateLabel: string;
  settingsLabel: string;
  deleteLabel: string;
  conditionalLabel: string;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  getLabel: (type: PdfComponentType) => string;
}) {
  const displayText = getComponentDisplayText(component);

  return (
    <Card
      className="p-3 cursor-move transition-colors hover:bg-muted/50"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          {(() => {
            const ComponentIcon = getComponentIcon(component.componentType);
            return <ComponentIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
          })()}
          <span className="text-sm font-medium">
            {getLabel(component.componentType as PdfComponentType)}
          </span>
          {displayText && <span className="text-sm text-muted-foreground">— {displayText}</span>}
          {hasConditionalDisplay(component.config) && (
            <Badge variant="secondary" className="text-xs">
              {conditionalLabel}
            </Badge>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            tooltip={settingsLabel}
            onClick={(e) => {
              e.stopPropagation();
              onSettings();
            }}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={duplicateLabel}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={deleteLabel}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
